/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Bar,
  HistoryMetadata,
  LibrarySymbolInfo,
  PeriodParams,
} from 'modules/TradingChart/components/TradingView/sources/charting_library.min';
import {
  BarsData,
} from 'modules/TradingChart/components/TradingView/utils/services/barsProviders/typings';
import ChartsStore from 'modules/TradingChart';
import { KOLKATA_TIMEZONE_DIFFERENCE_IN_MILLISECONDS, ServerIntervals } from 'modules/TradingChart/consts';
import FetchService from 'modules/Fetch';
import Validation from 'modules/Validation/service';
import TradingChartService from 'modules/TradingChart/service';
import SearchService from 'modules/Search';
import FormatService from 'modules/Format';
import {
  ONE_MINUTE_CANDLES_FETCH_LIMIT,
  ONE_DAY_CANDLES_FETCH_LIMIT,
  IBarsProviderRest,
} from 'modules/TradingChart/components/TradingView/utils/services/barsProviders/BarsProviderRest/consts';
import { decomposeExchangeToken, decomposeSegment } from 'modules/BOD/modules/Search/utils';

dayjs.extend(utc);

class BarsProviderRest implements IBarsProviderRest {
  private getInterval = (resolution: string) => {
    if (Number.isInteger(+resolution)) {
      return `I${resolution}`;
    }

    return resolution;
  };

  public fetchHistoricalBars = async (
    instrumentKey: string,
    interval: string,
    previousDateTo: number,
    limit: number,
  ) => {
    const res = await FetchService.request<'CHARTS_DATA', BarsResponseApiVersion2>(
      'CHARTS_DATA',
      '/candles',
      'CHARTS_DATA',
      {
      query: {
        instrumentKey,
        interval,
        from: previousDateTo,
        limit,
      },
      },
    );

    return res;
  }

  public async getHistoricalBars(
    symbolInfo: Partial<LibrarySymbolInfo>,
    resolution: string,
    periodParams: PeriodParams,
  ): Promise<BarsData> {
    const {
      isContinousChartChecked,
      isContinuousChartsApplicable,
      continousChartInstrumentKeys,
    } = ChartsStore;
    const { ticker = '' } = symbolInfo;
    const customOIIndicator = ticker.includes('#customOI');
    const currentSymbol = FormatService.decodeCustomTvExchangeToken(
      ticker,
    );
    const { segment } = decomposeExchangeToken(ticker);

    const interval = this.getInterval(resolution);
    const symbolInterval = `${ticker}_#_${interval}`;

    const CANDLES_FETCH_LIMIT = interval === '1D' ? ONE_DAY_CANDLES_FETCH_LIMIT : ONE_MINUTE_CANDLES_FETCH_LIMIT;

    // if request is sending not for the first time for the same interval,
    // we need to use the timestamp from previous request
    const historicalTimestamp =
      ChartsStore.historicalDataRequestTimestamp[symbolInterval];

    // Use dayjs to get the end of day timestamp
    const endOfDayTimestamp = dayjs().endOf('day').valueOf();

    // For first data request, we need to fetch data for current timestamp
    const previousDateTo = periodParams?.firstDataRequest ? endOfDayTimestamp
    : historicalTimestamp || endOfDayTimestamp;

    // If prevTimestamp from prev request is 0, then no data is available
    if (historicalTimestamp === -1 && !periodParams?.firstDataRequest) {
      // Resetting the timestamp to current date to make request for next time load of charts
      ChartsStore.setHistoricalDataRequestTimestamp(
        symbolInterval, previousDateTo);
      return new Promise((resolve) => {
        resolve({ bars: [], meta: { noData: true, nextTime: 0 } });
      });
    }

    // if it is minute interval the first request must be intraday to get data for current day only
    const isIntradayRequest =
      periodParams.firstDataRequest &&
      [ServerIntervals.ONE_MINUTE].includes(resolution as ServerIntervals);
    // const requestType = isIntradayRequest ? 'intraday' : 'historical';
    const scripData = await SearchService.searchByExchangeToken(currentSymbol || '');

    const continousChartInstrumentKey = continousChartInstrumentKeys[scripData?.exchangeToken || ''];
    const isContinuousChartsApplicableForSymbol = isContinuousChartsApplicable[scripData?.exchangeToken || ''];

    const continuousChartToken = `${scripData?.exchangeToken || ''}_${resolution.toLowerCase()}`;

    const instrumentKey =
      isContinousChartChecked[continuousChartToken] &&
        isContinuousChartsApplicableForSymbol &&
        !isIntradayRequest
        ? continousChartInstrumentKey
        : scripData?.instrumentKey || '';

    const result = await this.fetchHistoricalBars(
      instrumentKey,
      interval,
      previousDateTo,
      CANDLES_FETCH_LIMIT,
    );

    if (!result.data) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject('Server responded with no data');
    }

    let candles = result?.data.candles;
    let prevTimestamp = result?.data.meta.prevTimestamp ?? -1;
    if (periodParams.firstDataRequest && candles?.length < 500) {
      const res = await this.fetchHistoricalBars(instrumentKey,
        interval,
        prevTimestamp,
        CANDLES_FETCH_LIMIT,
      );
      candles = [...candles, ...(res?.data?.candles || [])];
      prevTimestamp = res?.data?.meta?.prevTimestamp ?? -1;
    }

    const { series } = decomposeSegment(segment);
    const isIndex = Validation.isIndex(series);

    const rawBars: Bar[] = candles
      .map((barData) =>
        TradingChartService.ohlcStringToBar(
          barData,
          isIndex,
          customOIIndicator,
        ),
      )
      .reverse();

    let requestedBars = rawBars;

    // Add IST time difference
    if (interval === '1D') {
      requestedBars = requestedBars.map(bar => ({
        ...bar,
        time: bar.time + KOLKATA_TIMEZONE_DIFFERENCE_IN_MILLISECONDS,
      }));
    }

    // If prevTimestamp is -1 and current candles is also empty,
    // then delete historical timestamp from reducer to make fresh request for
    // next time load of charts
    if (prevTimestamp === -1 && !requestedBars.length) {
      ChartsStore.deleteHistoricalDataRequestTimestamp(symbolInterval);
    } else {
      ChartsStore.setHistoricalDataRequestTimestamp(
        symbolInterval,
        prevTimestamp,
      );
    }

    const meta: HistoryMetadata = {
      noData: !requestedBars.length && prevTimestamp === -1,
    };

    // send nextTime only when candles are empty and prevTimestamp is not null
    if (!requestedBars.length && prevTimestamp !== -1) {
      meta.nextTime = prevTimestamp;
    }

    return Promise.resolve({ bars: requestedBars, meta });
  }
}

export default new BarsProviderRest();
